<template>
  <div class="page-wrapper">
    <transition name="rotate" mode="out-in">
      <div :key="1" v-if="!state.isSubmitted" class="top-spacing">
        <h1 class="page-title">Enter Mobile Number</h1>
        <br />
        <input
          class="text-input"
          :disabled="state.otpSent"
          type="tel"
          maxlength="10"
          v-model="mobileForm.fields.mobile"
          placeholder="10 digit mobile number"
          @keypress="isNumber($event)"
          @blur="validateMobileFormField"
        />
        <FormFieldError class="mt-2" :errors="mobileForm.errors.mobile" />
        <FormFieldError class="mt-2" :errors="mobileForm.errors.event" />

        <div class="mt-2 mb-4 text-xs">
          (One mobile number can only be registered once)
        </div>
        <div
          style="margin-top: 32px"
          class="text-center"
          v-show="!state.otpSent"
        >
          <button
            class="button-blue"
            @click="sendOTP"
            :disabled="state.isInProgress"
            :class="{
              'button-blue--loading': state.isInProgress,
              'btn-disabled': !isMobileNumberValid,
            }"
          >
            <span>SEND OTP</span>
          </button>
        </div>
        <br />
        <br />
        <br />
        <div v-if="state.otpSent">
          <div class="page-title">Enter 4-digit OTP sent</div>
          <br />

          <input
            class="text-input"
            maxlength="4"
            v-model="otpForm.fields.otp"
            placeholder="OTP"
            @blur="validateOTPField"
            @input="
              (val) => (otpForm.fields.otp = otpForm.fields.otp.toUpperCase())
            "
          />
          <FormFieldError class="mt-2" :errors="otpForm.errors.otp" />
          <br />
          <br />
          <div class="text-center">
            <button
              :disabled="state.isInProgress"
              :class="{ 'button-blue--loading': state.isInProgress }"
              class="button-blue"
              @click="verifyOtp"
            >
              <span> Verify OTP</span>
            </button>
          </div>
          <br />
          <div v-if="state.canResendOTP">
            <div
              class="font-akhand text-primaryBlue"
              @click="resendOTP"
              v-if="!state.otpResent"
            >
              Resend OTP
            </div>
            <div v-else class="otp-resent-done">
              OTP Resent on {{ mobileForm.fields.mobile }}
            </div>
          </div>
          <div v-else>
            Resend OTP in {{ clock.timer.minutes }}:{{ clock.timer.seconds }}
          </div>
        </div>
        <div class="guard-wrapper">
          <img
            src="@/assets/images/image-v2/guard-razor.png"
            srcset="
              @/assets/images/image-v2/guard-razor.png 1x,
              @/assets/images/guard-razor@2x.png       2x
            "
            alt="guard razor"
          />
        </div>
      </div>
      <div :key="2" class="verified-stage top-spacing" v-else>
        <img src="@/assets/images/embelem-hand.svg" alt="embelem-hand" />
        <br />
        <br />
        <div class="title">
          Mobile number
          <br />verified successfully
        </div>
      </div>
    </transition>
    <Footer />
  </div>
</template>

<script setup>
import { reactive, computed } from 'vue'
import { useRouter } from 'vue-router'
import useValidateForm from '@/hooks/useValidateForm'
import FormFieldError from '@/components/FormFieldError.vue'
import Footer from '@/components/Footer.vue'
import useTimer from '@/hooks/useTimer'
import useEventCheck from '@/hooks/useEventCheck'

import { sendOtpApi, verifyOTPApi } from '../api'

const { push } = useRouter()

useEventCheck()

const state = reactive({
  isSubmitted: false,
  otpSent: false,
  otpResent: false,
  isInProgress: false,
  canResendOTP: false,
})

const isNumber = (evt) => {
  evt = evt ? evt : window.event
  var charCode = evt.which ? evt.which : evt.keyCode
  if (charCode < 48 || charCode > 57) {
    evt.preventDefault()
  }
}

/* Mobile Form */
const mobileForm = reactive({
  fields: {
    mobile: null,
  },
  errors: {
    mobile: [],
  },
  validations: {
    mobile: [
      { name: 'required', message: 'Please enter your mobile number' },
      {
        name: 'mobile',
        message: 'Please enter a valid 10 digit mobile number',
      },
    ],
  },
})

const {
  validateForm: validateMobileForm,
  validateField: validateMobileFormField,
} = useValidateForm(mobileForm)

const { startTimer, clock } = useTimer()

const isMobileNumberValid = computed(() => {
  return validateMobileForm(false)
})

const sendOTP = async () => {
  const isValid = validateMobileForm()
  if (!isValid) return
  state.isInProgress = true
  const { status, data } = await sendOtpApi(mobileForm.fields.mobile)
  state.isInProgress = false
  if (status === 200) {
    startTimer(() => {
      state.canResendOTP = true
    })
    state.otpSent = true
  }
  if (status === 400) {
    Object.assign(mobileForm.errors, data)
  }
}

const resendOTP = () => {
  state.otpResent = true
  sendOTP()
}

/** OTP Form */
const otpForm = reactive({
  fields: {
    otp: '',
  },
  errors: {
    otp: [],
  },
  validations: {
    otp: [
      { name: 'required', message: 'Please enter OTP' },
      {
        name: 'minLength',
        minLength: 4,
        message: 'Please enter a valid 4 digit mobile',
      },
    ],
  },
})

const { validateForm: validateOTPForm, validateField: validateOTPField } =
  useValidateForm(otpForm)

async function verifyOtp() {
  const isValid = validateOTPForm()
  if (!isValid) return
  state.isInProgress = true
  const { status, data } = await verifyOTPApi({
    mobile: mobileForm.fields.mobile,
    otp: otpForm.fields.otp,
  })
  state.isInProgress = false
  if (status === 200) {
    state.isSubmitted = true
    if (window?.safalta_config) {
      window.safalta_config.IS_LOGGED_IN = true
    }
    push({ name: 'pii' })
  }
  if (status === 400) {
    Object.assign(otpForm.errors, data)
  }
}
</script>
<style scoped>
.page-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-direction: column;
}
.top-spacing {
  width: 100%;
}
.verified-stage {
  height: 100%;
  margin-bottom: 200px;
}
.otp-resent-done {
  color: green;
  font-size: 12px;
  display: block;
  text-align: center;
}
.guard-wrapper {
  display: flex;
  img {
    margin-left: auto;
    margin-right: 30px;
    margin-bottom: 30px;
  }
}
</style>
